import React, { useEffect, useState, useRef } from "react";
import Profile from "../Components/Profile";
import useAccessToken from "../Custom/useAccessToken";
import { searchProfile } from "../Services/GetService";
import { useLocation } from "react-router-dom";
import { Skeleton } from "primereact/skeleton";
import ICONS from "../Constants/Icons.tsx";
import { Paginator } from "primereact/paginator";

interface SearchProps {
  searchQuery: string;
}

const Search: React.FC<SearchProps> = ({ searchQuery }) => {

    const Icon = ICONS();
    const { accessToken } = useAccessToken();
    const [results, setResults] = useState([]);
    const [isSearching, setIsSearching] = useState(false);
    const [loading, setLoading] = useState(false);
    const [first, setFirst] = useState(0);
    const [rows] = useState(25);
    // Ref to scroll the list back to the top when the page changes
    const listRef = useRef<HTMLDivElement | null>(null);

    const location = useLocation();
    const navState = location.state as { from?: string; query?: string };

    useEffect(() => {
        if (searchQuery && searchQuery.trim() !== "") {
            setIsSearching(true);
            searchProfiles(searchQuery, null);
        } else {
            setIsSearching(false);
        }
    }, [searchQuery]);

    useEffect(() => {
        if (!accessToken) return;

        if (navState?.from === "search" && navState.query) {
            setIsSearching(true);
            searchProfiles(navState.query, null);
        } else {
            setIsSearching(false);
        }
    }, [accessToken, navState]);

    const searchProfiles = async (query: any, topResults?: any) => {
        setLoading(true);
        try {
            const res = await searchProfile(
                accessToken,
                encodeURIComponent(query),
                topResults
            );
            setResults(res || []);
        } catch (err) {
            console.error("Error occured while searching:", err);
            setResults([]);
        }
        setLoading(false);
    }; 

    const ProfileSkeleton = () => (
        <div className="profile-card">
            <div className="profile-left">
                <Skeleton shape="circle" size="80px" />
                <div className="profile-info">
                    <Skeleton
                        width="180px"
                        height="1.2rem"
                        className="profile-name mb-2"
                    />
                    <Skeleton
                        width="150px"
                        height="1rem"
                        className="profile-title mb-2"
                    />
                </div>
            </div>
            <div className="profile-right">
                <Skeleton width="160px" height="1rem" className="profile-meta mb-2" />
                <Skeleton width="130px" height="1rem" className="profile-meta mb-2" />
            </div>
        </div>
    );

    return (
        <>
            <div
                className="p-3 mt-6 surface-ground  main-contain-home"
                style={{ height: "calc(100vh - 72px)" }}
            >
                <div className="mx-auto" style={{ maxWidth: "1040px" }}>                  

                    <div className="section-header mb-2">
                        {isSearching ? (
                            <span className="section-icon">
                                <img src={Icon.People} />
                            </span>
                        ) : (
                            <></>
                        )}
                        <span className="section-title">
                            {isSearching ? "Search Results" : ""}
                        </span>
                    </div>

                    {loading ? (
                        <div className="profiles-list">
                            {Array.from({ length: 4 }).map((_, i) => (
                                <ProfileSkeleton key={i} />
                            ))}
                        </div>
                    ) : results.length === 0 && isSearching ? (
                        <div className="no-search-results">
                            <h4>We didn’t find any matches.</h4>
                            <span>
                                Try broader or alternative search terms such as name, role,
                                office, or skill.
                            </span>
                        </div>
                    ) : (
                        <div className="search-results-container">
                            <div
                                className="profiles-list-with-pagination"
                                ref={listRef}
                            >
                                {results
                                    ?.slice(first, first + rows)
                                    .map((person: any, index: any) => (
                                        <div key={index} className="cursor-pointer">
                                            <Profile
                                                key={index}
                                                {...person}
                                                isSearching={isSearching}
                                                searchQuery={searchQuery}
                                            />
                                        </div>
                                    ))}
                            </div>

                            {results.length > rows && (
                                <div className="pagination-container">
                                    <Paginator
                                        first={first}
                                        rows={rows}
                                        totalRecords={results.length}
                                        onPageChange={(e) => {
                                            setFirst(e.first);
                                            // Scroll the container (not the whole window) to the top
                                            if (listRef.current) {
                                                listRef.current.scrollTo({ top: 0, behavior: 'smooth' });
                                            }
                                        }}
                                        className="custom-paginator"
                                    />
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </>
    );
};

export default Search;